// @ts-nocheck
/**
 * Backup & Disaster Recovery Dashboard
 * Comprehensive backup management interface for System Administrators
 * Implements epic75-system-backup-disaster-recovery requirements
 */

import React, { useEffect, useState } from 'react';
import {
  Grid,
  Column,
  Heading,
  Tile,
  DataTable,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableHeader,
  TableBody,
  TableCell,
  Button,
  Loading,
  InlineNotification,
  Tag,
  Modal,
  Tabs,
  TabList,
  Tab,
  TabPanels,
  TabPanel,
  ProgressIndicator,
  ProgressStep,
  Accordion,
  AccordionItem,
  DatePicker,
  DatePickerInput,
  Select,
  SelectItem,
  Stack,
} from '@carbon/react';
import {
  Save,
  PlayFilled,
  Restart,
  Warning,
  CheckmarkFilled,
  Misuse,
  Download,
  DataBackup,
  Dashboard as DashboardIcon,
} from '@carbon/icons-react';
import axios from 'axios';
import styles from './BackupDashboard.module.scss';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api';

interface BackupMetrics {
  total_backups: number;
  successful_backups: number;
  failed_backups: number;
  total_storage_gb: number;
  avg_backup_duration_minutes: number;
  last_successful_backup?: string;
  rto_compliance_percentage: number;
  rpo_compliance_percentage: number;
}

interface BackupJob {
  id: string;
  job_type: string;
  status: string;
  started_at: string;
  completed_at?: string;
  backup_size_bytes?: number;
  source_identifier: string;
  target_location: string;
  error_message?: string;
}

interface DisasterRecoveryEvent {
  id: string;
  event_type: string;
  severity: string;
  affected_services: string;
  recovery_status: string;
  started_at: string;
  completed_at?: string;
  rto_minutes?: number;
  rpo_minutes?: number;
}

interface HealthCheck {
  overall_status: string;
  d1_connection: boolean;
  r2_storage: boolean;
  kv_access: boolean;
  backup_orchestrator: boolean;
  last_checked: string;
}

interface StorageUsage {
  total_size_bytes: number;
  by_type: Record<string, number>;
  retention_policy_applied: boolean;
}

const BackupDashboard: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState(0);

  // State for backup data
  const [metrics, setMetrics] = useState<BackupMetrics | null>(null);
  const [backupHistory, setBackupHistory] = useState<BackupJob[]>([]);
  const [drEvents, setDrEvents] = useState<DisasterRecoveryEvent[]>([]);
  const [health, setHealth] = useState<HealthCheck | null>(null);
  const [storage, setStorage] = useState<StorageUsage | null>(null);
  const [alerts, setAlerts] = useState<any[]>([]);

  // Modal states
  const [scheduleModalOpen, setScheduleModalOpen] = useState(false);
  const [drModalOpen, setDrModalOpen] = useState(false);
  const [scheduleJobType, setScheduleJobType] = useState('full_system');
  const [drEventType, setDrEventType] = useState('manual_test');
  const [schedulingBackup, setSchedulingBackup] = useState(false);
  const [triggeringDR, setTriggeringDR] = useState(false);

  // Load all backup data
  useEffect(() => {
    loadBackupData();
    // Refresh data every 30 seconds
    const interval = setInterval(loadBackupData, 30000);
    return () => clearInterval(interval);
  }, []);

  const loadBackupData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Load all data in parallel
      const [statusRes, historyRes, eventsRes, storageRes] = await Promise.all([
        axios.get(`${API_BASE_URL}/v1/backup/status`),
        axios.get(`${API_BASE_URL}/v1/backup/history?limit=50`),
        axios.get(`${API_BASE_URL}/v1/backup/disaster-recovery/events?limit=20`),
        axios.get(`${API_BASE_URL}/v1/backup/storage`),
      ]);

      if (statusRes.data.success) {
        setMetrics(statusRes.data.metrics);
        setHealth(statusRes.data.health);
        setAlerts(statusRes.data.alerts || []);
      }

      if (historyRes.data.success) {
        setBackupHistory(historyRes.data.history);
      }

      if (eventsRes.data.success) {
        setDrEvents(eventsRes.data.events);
      }

      if (storageRes.data.success) {
        setStorage(storageRes.data.storage);
      }
    } catch (err: any) {
      console.error('Failed to load backup data:', err);
      setError(err.response?.data?.error || err.message || 'Failed to load backup data');
    } finally {
      setLoading(false);
    }
  };

  const handleScheduleBackup = async () => {
    try {
      setSchedulingBackup(true);
      setError(null);

      await axios.post(`${API_BASE_URL}/v1/backup/schedule`, {
        jobType: scheduleJobType,
        metadata: {
          source: 'manual_ui_trigger',
          timestamp: new Date().toISOString(),
        },
      });

      setScheduleModalOpen(false);
      // Reload data to show new backup job
      await loadBackupData();
    } catch (err: any) {
      console.error('Failed to schedule backup:', err);
      setError(err.response?.data?.error || err.message || 'Failed to schedule backup');
    } finally {
      setSchedulingBackup(false);
    }
  };

  const handleTriggerDisasterRecovery = async () => {
    try {
      setTriggeringDR(true);
      setError(null);

      await axios.post(`${API_BASE_URL}/v1/backup/disaster-recovery`, {
        eventType: drEventType,
        severity: drEventType === 'manual_test' ? 'low' : 'high',
        affectedServices: ['d1', 'kv', 'r2'],
      });

      setDrModalOpen(false);
      // Reload data to show new DR event
      await loadBackupData();
    } catch (err: any) {
      console.error('Failed to trigger disaster recovery:', err);
      setError(err.response?.data?.error || err.message || 'Failed to trigger disaster recovery');
    } finally {
      setTriggeringDR(false);
    }
  };

  const formatBytes = (bytes?: number): string => {
    if (!bytes) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${(bytes / Math.pow(k, i)).toFixed(2)} ${sizes[i]}`;
  };

  const formatDuration = (minutes?: number): string => {
    if (!minutes) return 'N/A';
    if (minutes < 60) return `${minutes}m`;
    return `${Math.floor(minutes / 60)}h ${minutes % 60}m`;
  };

  const getStatusTag = (status: string) => {
    switch (status.toLowerCase()) {
      case 'completed':
      case 'success':
      case 'healthy':
        return (
          <Tag type="green" renderIcon={CheckmarkFilled}>
            {status}
          </Tag>
        );
      case 'in_progress':
      case 'pending':
      case 'scheduled':
        return (
          <Tag type="blue" renderIcon={PlayFilled}>
            {status}
          </Tag>
        );
      case 'failed':
      case 'error':
      case 'unhealthy':
        return (
          <Tag type="red" renderIcon={Misuse}>
            {status}
          </Tag>
        );
      case 'warning':
      case 'degraded':
        return (
          <Tag type="yellow" renderIcon={Warning}>
            {status}
          </Tag>
        );
      default:
        return <Tag>{status}</Tag>;
    }
  };

  const getSeverityTag = (severity: string) => {
    switch (severity.toLowerCase()) {
      case 'critical':
      case 'high':
        return <Tag type="red">{severity}</Tag>;
      case 'medium':
        return <Tag type="yellow">{severity}</Tag>;
      case 'low':
        return <Tag type="blue">{severity}</Tag>;
      default:
        return <Tag>{severity}</Tag>;
    }
  };

  if (loading && !metrics) {
    return (
      <div style={{ padding: 'var(--cds-spacing-05)', textAlign: 'center' }}>
        <Loading withOverlay={false} description="Loading backup data..." />
      </div>
    );
  }

  return (
    <div className={styles.backupDashboard}>
      <Grid fullWidth>
        <Column lg={16} md={8} sm={4}>
          <div style={{ marginBottom: 'var(--cds-spacing-05)' }}>
            <Heading style={{ marginBottom: 'var(--cds-spacing-03)' }}>Backup & Disaster Recovery</Heading>
            <p style={{ color: 'var(--cds-text-secondary)' }}>
              Comprehensive backup management and disaster recovery operations
            </p>
          </div>

          {error && (
            <InlineNotification
              kind="error"
              title="Error"
              subtitle={error}
              onCloseButtonClick={() => setError(null)}
              style={{ marginBottom: 'var(--cds-spacing-05)' }}
            />
          )}

          {alerts.length > 0 && (
            <InlineNotification
              kind="warning"
              title="Active Alerts"
              subtitle={`${alerts.length} alert(s) require attention`}
              style={{ marginBottom: 'var(--cds-spacing-05)' }}
            />
          )}

          {/* Key Metrics */}
          <Grid narrow style={{ marginBottom: 'var(--cds-spacing-06)' }}>
            <Column lg={4} md={4} sm={4}>
              <Tile className={styles.metricTile}>
                <div className={styles.metricValue}>{metrics?.total_backups || 0}</div>
                <div className={styles.metricLabel}>Total Backups</div>
                <div className={styles.metricSubtext}>{metrics?.successful_backups || 0} successful</div>
              </Tile>
            </Column>
            <Column lg={4} md={4} sm={4}>
              <Tile className={styles.metricTile}>
                <div className={styles.metricValue}>
                  {formatBytes(metrics?.total_storage_gb ? metrics.total_storage_gb * 1024 * 1024 * 1024 : 0)}
                </div>
                <div className={styles.metricLabel}>Storage Used</div>
                <div className={styles.metricSubtext}>Across all backup types</div>
              </Tile>
            </Column>
            <Column lg={4} md={4} sm={4}>
              <Tile className={styles.metricTile}>
                <div className={styles.metricValue}>{metrics?.rto_compliance_percentage || 0}%</div>
                <div className={styles.metricLabel}>RTO Compliance</div>
                <div className={styles.metricSubtext}>Target: &lt;4 hours</div>
              </Tile>
            </Column>
            <Column lg={4} md={4} sm={4}>
              <Tile className={styles.metricTile}>
                <div className={styles.metricValue}>{metrics?.rpo_compliance_percentage || 0}%</div>
                <div className={styles.metricLabel}>RPO Compliance</div>
                <div className={styles.metricSubtext}>Target: &lt;1 hour</div>
              </Tile>
            </Column>
          </Grid>

          {/* System Health Status */}
          {health && (
            <Tile style={{ marginBottom: 'var(--cds-spacing-06)' }}>
              <Heading style={{ marginBottom: 'var(--cds-spacing-04)', fontSize: '1.25rem' }}>System Health</Heading>
              <Grid narrow>
                <Column lg={4} md={4} sm={4}>
                  <div style={{ marginBottom: 'var(--cds-spacing-03)' }}>
                    D1 Database: {getStatusTag(health.d1_connection ? 'healthy' : 'unhealthy')}
                  </div>
                </Column>
                <Column lg={4} md={4} sm={4}>
                  <div style={{ marginBottom: 'var(--cds-spacing-03)' }}>
                    R2 Storage: {getStatusTag(health.r2_storage ? 'healthy' : 'unhealthy')}
                  </div>
                </Column>
                <Column lg={4} md={4} sm={4}>
                  <div style={{ marginBottom: 'var(--cds-spacing-03)' }}>
                    KV Access: {getStatusTag(health.kv_access ? 'healthy' : 'unhealthy')}
                  </div>
                </Column>
                <Column lg={4} md={4} sm={4}>
                  <div style={{ marginBottom: 'var(--cds-spacing-03)' }}>
                    Backup System: {getStatusTag(health.backup_orchestrator ? 'healthy' : 'unhealthy')}
                  </div>
                </Column>
              </Grid>
              <div
                style={{ marginTop: 'var(--cds-spacing-04)', color: 'var(--cds-text-secondary)', fontSize: '0.875rem' }}
              >
                Last checked: {new Date(health.last_checked).toLocaleString()}
              </div>
            </Tile>
          )}

          {/* Action Buttons */}
          <div style={{ marginBottom: 'var(--cds-spacing-06)', display: 'flex', gap: 'var(--cds-spacing-03)' }}>
            <Button kind="primary" renderIcon={DataBackup} onClick={() => setScheduleModalOpen(true)}>
              Schedule Backup
            </Button>
            <Button kind="secondary" renderIcon={Restart} onClick={() => setDrModalOpen(true)}>
              Test Disaster Recovery
            </Button>
            <Button kind="tertiary" renderIcon={Download} onClick={loadBackupData}>
              Refresh Data
            </Button>
          </div>

          {/* Tabbed Content */}
          <Tabs selectedIndex={activeTab} onChange={e => setActiveTab(e.selectedIndex)}>
            <TabList aria-label="Backup management tabs">
              <Tab>Backup History</Tab>
              <Tab>Disaster Recovery</Tab>
              <Tab>Storage Details</Tab>
            </TabList>
            <TabPanels>
              {/* Backup History Tab */}
              <TabPanel>
                <DataTable
                  rows={backupHistory.map((job, idx) => ({
                    id: job.id || `job-${idx}`,
                    ...job,
                  }))}
                  headers={[
                    { key: 'job_type', header: 'Backup Type' },
                    { key: 'status', header: 'Status' },
                    { key: 'started_at', header: 'Started' },
                    { key: 'backup_size_bytes', header: 'Size' },
                    { key: 'source_identifier', header: 'Source' },
                  ]}
                >
                  {({ rows, headers, getTableProps, getHeaderProps, getRowProps }) => (
                    <TableContainer title="Recent Backup Jobs">
                      <Table {...getTableProps()}>
                        <TableHead>
                          <TableRow>
                            {headers.map(header => (
                              <TableHeader {...getHeaderProps({ header })} key={header.key}>
                                {header.header}
                              </TableHeader>
                            ))}
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {rows.map(row => {
                            const job = backupHistory.find(j => j.id === row.id);
                            return (
                              <TableRow {...getRowProps({ row })} key={row.id}>
                                <TableCell>{job?.job_type || 'N/A'}</TableCell>
                                <TableCell>{job && getStatusTag(job.status)}</TableCell>
                                <TableCell>{job ? new Date(job.started_at).toLocaleString() : 'N/A'}</TableCell>
                                <TableCell>{formatBytes(job?.backup_size_bytes)}</TableCell>
                                <TableCell>{job?.source_identifier || 'N/A'}</TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )}
                </DataTable>
              </TabPanel>

              {/* Disaster Recovery Tab */}
              <TabPanel>
                <DataTable
                  rows={drEvents.map((event, idx) => ({
                    id: event.id || `dr-${idx}`,
                    ...event,
                  }))}
                  headers={[
                    { key: 'event_type', header: 'Event Type' },
                    { key: 'severity', header: 'Severity' },
                    { key: 'recovery_status', header: 'Status' },
                    { key: 'started_at', header: 'Started' },
                    { key: 'rto_minutes', header: 'RTO' },
                  ]}
                >
                  {({ rows, headers, getTableProps, getHeaderProps, getRowProps }) => (
                    <TableContainer title="Disaster Recovery Events">
                      <Table {...getTableProps()}>
                        <TableHead>
                          <TableRow>
                            {headers.map(header => (
                              <TableHeader {...getHeaderProps({ header })} key={header.key}>
                                {header.header}
                              </TableHeader>
                            ))}
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {rows.map(row => {
                            const event = drEvents.find(e => e.id === row.id);
                            return (
                              <TableRow {...getRowProps({ row })} key={row.id}>
                                <TableCell>{event?.event_type || 'N/A'}</TableCell>
                                <TableCell>{event && getSeverityTag(event.severity)}</TableCell>
                                <TableCell>{event && getStatusTag(event.recovery_status)}</TableCell>
                                <TableCell>{event ? new Date(event.started_at).toLocaleString() : 'N/A'}</TableCell>
                                <TableCell>{formatDuration(event?.rto_minutes)}</TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )}
                </DataTable>
              </TabPanel>

              {/* Storage Details Tab */}
              <TabPanel>
                {storage && (
                  <Tile>
                    <Heading style={{ marginBottom: 'var(--cds-spacing-04)', fontSize: '1.25rem' }}>
                      Storage Usage by Type
                    </Heading>
                    <div style={{ marginBottom: 'var(--cds-spacing-04)' }}>
                      <strong>Total Storage:</strong> {formatBytes(storage.total_size_bytes)}
                    </div>
                    {storage.by_type && Object.keys(storage.by_type).length > 0 ? (
                      <Grid narrow>
                        {Object.entries(storage.by_type).map(([type, size]) => (
                          <Column lg={8} md={4} sm={4} key={type}>
                            <div style={{ marginBottom: 'var(--cds-spacing-03)' }}>
                              <strong>{type}:</strong> {formatBytes(size as number)}
                            </div>
                          </Column>
                        ))}
                      </Grid>
                    ) : (
                      <p style={{ color: 'var(--cds-text-secondary)' }}>No storage breakdown available</p>
                    )}
                    <div style={{ marginTop: 'var(--cds-spacing-04)', color: 'var(--cds-text-secondary)' }}>
                      Retention policy: {storage.retention_policy_applied ? 'Active' : 'Inactive'}
                    </div>
                  </Tile>
                )}
              </TabPanel>
            </TabPanels>
          </Tabs>
        </Column>
      </Grid>

      {/* Schedule Backup Modal */}
      <Modal
        open={scheduleModalOpen}
        onRequestClose={() => setScheduleModalOpen(false)}
        modalHeading="Schedule New Backup"
        primaryButtonText="Schedule Backup"
        secondaryButtonText="Cancel"
        onRequestSubmit={handleScheduleBackup}
        primaryButtonDisabled={schedulingBackup}
      >
        <p style={{ marginBottom: 'var(--cds-spacing-05)' }}>Schedule a new backup job to run immediately.</p>
        <Select
          id="job-type-select"
          labelText="Backup Type"
          value={scheduleJobType}
          onChange={e => setScheduleJobType(e.target.value)}
        >
          <SelectItem value="full_system" text="Full System Backup" />
          <SelectItem value="d1_export" text="D1 Database Export" />
          <SelectItem value="kv_sync" text="KV Namespace Sync" />
          <SelectItem value="r2_replicate" text="R2 Bucket Replication" />
        </Select>
      </Modal>

      {/* Disaster Recovery Modal */}
      <Modal
        open={drModalOpen}
        onRequestClose={() => setDrModalOpen(false)}
        modalHeading="Trigger Disaster Recovery"
        primaryButtonText="Trigger Recovery"
        secondaryButtonText="Cancel"
        onRequestSubmit={handleTriggerDisasterRecovery}
        primaryButtonDisabled={triggeringDR}
        danger={drEventType !== 'manual_test'}
      >
        <p style={{ marginBottom: 'var(--cds-spacing-05)' }}>
          Trigger a disaster recovery event for testing or actual recovery.
        </p>
        <Select
          id="dr-event-type-select"
          labelText="Event Type"
          value={drEventType}
          onChange={e => setDrEventType(e.target.value)}
        >
          <SelectItem value="manual_test" text="Manual Test (Safe)" />
          <SelectItem value="data_corruption" text="Data Corruption" />
          <SelectItem value="service_outage" text="Service Outage" />
          <SelectItem value="regional_failure" text="Regional Failure" />
        </Select>
      </Modal>
    </div>
  );
};

export default BackupDashboard;
